/**
 * What's new (/changelog). The read-only changelog, reached from the avatar menu.
 *
 * Entries are written in Cockpit (IT > Changelog) and this page shows the Sweet
 * Spot feed, which includes anything published to both sites. Open to anyone with
 * a session, which is exactly the population that can see the avatar menu.
 *
 * Per Steven (2026-08-10) it uses the shared responsive grid rather than a card
 * stack, so it reads as a table on a desktop and as cards on a phone. That makes
 * this the site's first `DataGridShell` consumer, so it supplies the scroll-root
 * selector itself (Cockpit has a local wrapper that defaults it; this repo does
 * not).
 */

import {
  DataGridShell,
  StateCard,
  gridTheadClassName,
  useIsMobile,
  formatBusinessDate,
} from '@ampersandbrands/ui';
import { HtmlBody } from '@/components/HtmlBody';
import { isNotAvailable } from '@/lib/useStores';
import { changelogToolKey, useChangelog, type ChangelogEntry } from '@/lib/useChangelog';

function ToolTags({ entry }: { entry: ChangelogEntry }) {
  const tags = entry.all_tools ? ['All tools'] : entry.tools.map((t) => t.tool_label);
  if (tags.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1">
      {tags.map((label, i) => (
        <span
          key={entry.all_tools ? label : changelogToolKey(entry.tools[i]!)}
          className="rounded-pill bg-candy-cream-warm px-2 py-0.5 text-[11px] font-medium text-candy-ink-muted dark:bg-candy-dark-surface-warm dark:text-candy-dark-ink-muted"
        >
          {label}
        </span>
      ))}
    </div>
  );
}

export function Changelog() {
  const isMobile = useIsMobile();
  const { data, isLoading, error } = useChangelog();
  const entries = data ?? [];

  const pageHead = (
    <div className="mb-4">
      <h1 className="text-xl font-semibold text-candy-ink dark:text-candy-dark-ink">
        What&apos;s new
      </h1>
      <p className="mt-0.5 text-sm text-candy-ink-muted dark:text-candy-dark-ink-muted">
        Recent changes to The Sweet Spot, newest first.
      </p>
    </div>
  );

  if (isLoading) {
    return (
      <div className="mx-auto w-full max-w-4xl pb-24">
        {pageHead}
        <div className="space-y-4">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="h-24 animate-pulse rounded-xl bg-candy-cream-warm dark:bg-candy-dark-surface-warm"
            />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto w-full max-w-4xl pb-24">
        {pageHead}
        <StateCard
          dashed={isNotAvailable(error)}
          tone={isNotAvailable(error) ? undefined : 'error'}
          title={
            isNotAvailable(error)
              ? "What's new isn't available yet"
              : "What's new could not be loaded"
          }
          description={
            isNotAvailable(error)
              ? 'Recent changes to the site will appear here once this is switched on.'
              : 'Something went wrong fetching the updates. Try again shortly.'
          }
        />
      </div>
    );
  }

  if (entries.length === 0) {
    return (
      <div className="mx-auto w-full max-w-4xl pb-24">
        {pageHead}
        <StateCard
          dashed
          title="Nothing new yet"
          description="Updates to The Sweet Spot will show up here as they ship."
        />
      </div>
    );
  }

  const renderCards = () => (
    <div className="space-y-3 py-2">
      {entries.map((e) => (
        <article
          key={e.id}
          className="rounded-xl border border-candy-line bg-candy-white p-4 shadow-1 dark:border-candy-dark-line dark:bg-candy-dark-surface"
        >
          <p className="text-[11px] font-medium uppercase tracking-wide text-candy-ink-muted dark:text-candy-dark-ink-muted">
            {formatBusinessDate(e.published_at)}
          </p>
          <h2 className="mt-0.5 text-[15px] font-semibold text-candy-ink dark:text-candy-dark-ink">
            {e.title}
          </h2>
          <div className="mt-2">
            <HtmlBody src={e.body_html} />
          </div>
          <div className="mt-2">
            <ToolTags entry={e} />
          </div>
        </article>
      ))}
    </div>
  );

  return (
    <div className="mx-auto flex h-full w-full max-w-4xl flex-col pb-24">
      {pageHead}
      <DataGridShell
        total={entries.length}
        loaded={entries.length}
        countNoun="updates"
        scrollRootSelector="[data-sweetspot-main]"
        renderCards={isMobile ? renderCards : undefined}
      >
        <table className="min-w-full text-sm">
          <thead className={gridTheadClassName}>
            <tr>
              <th className="w-[120px] px-3 py-2 text-left font-semibold">Date</th>
              <th className="w-[240px] px-3 py-2 text-left font-semibold">Title</th>
              <th className="px-3 py-2 text-left font-semibold">What changed</th>
              <th className="w-[180px] px-3 py-2 text-left font-semibold">Tools</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-candy-line dark:divide-candy-dark-line">
            {entries.map((e) => (
              <tr key={e.id}>
                <td className="px-3 py-3 align-top text-candy-ink-muted dark:text-candy-dark-ink-muted">
                  {formatBusinessDate(e.published_at)}
                </td>
                <td className="px-3 py-3 align-top font-semibold text-candy-ink dark:text-candy-dark-ink">
                  {e.title}
                </td>
                <td className="px-3 py-3 align-top">
                  <HtmlBody src={e.body_html} />
                </td>
                <td className="px-3 py-3 align-top">
                  <ToolTags entry={e} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </DataGridShell>
    </div>
  );
}
